<script setup>
import { ref, reactive } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useVModel } from '@vueuse/core'
import { DxPopup } from 'devextreme-vue/popup'

import { useNotify } from '@/composables/notify'
import { useKeyboard } from '@/composables/keyboard'
import { useImportRowWorkflow } from '@/composables/import-row-workflow'

import { usePortalStore } from '@/stores/portal.store'

import ClipboardGroupGrid from '@/components/sale/ClipboardGroupGrid.vue'
import RowImportGrid from '@/components/shared/RowImportGrid.vue'

const props = defineProps({
  gridId: {
    type: String,
    default: null,
  },
  columns: {
    type: Array,
    default: () => [],
  },
  visible: {
    type: Boolean,
    default: false,
  },
  selectedRows: {
    type: Array,
    default: null,
  },
  sourceIncludesVat: {
    type: Boolean,
    default: false,
  },
  documentType: {
    type: Number,
    default: null,
  },
  entityLock: {
    type: Object,
    default: null,
  },
  isReadOnlyRow: {
    type: Boolean,
    default: false,
  }
})

const emit = defineEmits(['update:visible', 'cancel', 'updated'])
const popupVisible = useVModel(props, 'visible', emit)
const i18n = getI18n()

const { notifyError, notifySuccess } = useNotify()
const { useDoubleEscape } = useKeyboard()
const portalStore = usePortalStore()

const workflowContext = reactive({ documentType: portalStore.documentTypesEnum.CLIPBOARD, documentId: null, isDocumentSource: true })

const {
  isMove,
  isValidating,
  isImporting,
  reviewDataSource,
  blockingValidationCount,
  canImport,
  validateAndImportAsync,
  importAsync,
  replaceReviewRowAsync,
  setSelectedReviewKeys,
  reset: resetWorkflow,
} = useImportRowWorkflow(workflowContext)

const step = ref(1)
const selectedClipboardGroup = ref(null)

const gridShow = ref(false)
const onShown = () => { gridShow.value = true }

const reset = () => {
  step.value = 1
  selectedClipboardGroup.value = null
  workflowContext.documentId = null
  gridShow.value = false
  resetWorkflow()
}

const close = () => {
  reset()
  popupVisible.value = false
}

const finishExport = () => {
  notifySuccess(i18n.t('common.exportSuccess'))
  close()
  emit('updated')
}

const buildSourceRows = () =>
  (props.selectedRows || []).map(row => ({
    ...row,
    source: props.documentType,
    source_includes_vat: props.sourceIncludesVat,
    keep_price_discount: true,
  }))

const onValidateClick = async(isMoveRequest) => {
  if (!selectedClipboardGroup.value?.length) return

  workflowContext.documentId = selectedClipboardGroup.value[0].id

  const { status } = await validateAndImportAsync(buildSourceRows(), isMoveRequest)

  if (status === 'imported') {
    finishExport()
    return
  }

  step.value = 2
}

const onImportClick = async() => {
  if (!canImport.value) return

  const { status } = await importAsync()

  if (status === 'imported') {
    finishExport()
    return
  }

  step.value = 2
  notifyError(i18n.t('error.importValidationError'))
}

const onBackClick = () => {
  isMove.value = false
  step.value = 1
}

const onHiding = () => {
  props.entityLock?.releaseLock()
  reset()
  emit('cancel')
}

const removeDoubleEscapeEventListener = useDoubleEscape(onHiding)

const onHidden = () => {
  removeDoubleEscapeEventListener()
}
</script>

<template>
  <dx-popup
    v-model:visible="popupVisible"
    :hide-on-outside-click="false"
    :title="i18n.t('common.exportToClipboard')"
    height="auto"
    max-height="80%"
    width="90%"
    :show-close-button="!isImporting && !isValidating"
    @hiding="onHiding"
    @hidden="onHidden"
    @shown="onShown"
  >
    <q-stepper
      v-model="step"
      color="primary"
      done-icon="mdi-check"
      done-color="positive"
      animated
      flat
    >
      <q-step
        :name="1"
        :title="i18n.t('common.selectClipboard')"
        icon="mdi-clipboard-text"
        :done="step > 1"
      >
        <clipboard-group-grid
          v-if="gridShow"
          v-model:selected-rows="selectedClipboardGroup"
          :grid-id="`${props.gridId}-select`"
          select-mode
          add-mode
        />
      </q-step>
      <q-step
        :name="2"
        :title="i18n.t('common.validationResult', 2)"
        icon="mdi-alert"
        color="warning"
        :disable="step < 2"
        :done="step > 2"
      >
        <row-import-grid
          style="max-height: 30rem;"
          :grid-id="`${props.gridId}-validation`"
          :columns="props.columns"
          :data-source="reviewDataSource"
          :entity-lock="props.entityLock"
          :document-id="workflowContext.documentId"
          :document-type="workflowContext.documentType"
          @update:selected-rows="replaceReviewRowAsync"
          @update:selected-keys="setSelectedReviewKeys"
        />
      </q-step>
      <template #navigation>
        <q-stepper-navigation class="column items-end">
          <div
            v-if="step > 1 && blockingValidationCount > 0"
            class="text-negative text-caption q-mb-sm"
          >
            {{ i18n.t('common.importRow.unresolvedIssues', { count: blockingValidationCount }) }}
          </div>
          <div>
            <q-btn
              v-if="step === 1"
              style="width: 9rem"
              dense
              class="q-ml-sm"
              :color="isValidating && !isMove ? 'warning' : 'primary'"
              :loading="isValidating && !isMove"
              :label="i18n.t('common.copy')"
              :disable="!selectedClipboardGroup?.length || isValidating"
              @click="() => onValidateClick(false)"
            >
              <template #loading>
                <div class="q-ph-md row">
                  <div>{{ i18n.t('common.validating') }}</div>
                  <q-spinner-dots class="on-right" />
                </div>
              </template>
            </q-btn>
            <q-btn
              v-if="step === 1 && !props.isReadOnlyRow"
              style="min-width: 9rem"
              dense
              class="q-ml-sm"
              :color="isValidating && isMove ? 'warning' : 'primary'"
              :loading="isValidating && isMove"
              :label="i18n.t('common.move')"
              :disable="!selectedClipboardGroup?.length || isValidating"
              @click="() => onValidateClick(true)"
            >
              <template #loading>
                <div class="q-ph-md row">
                  <div>{{ i18n.t('common.validating') }}</div>
                  <q-spinner-dots class="on-right" />
                </div>
              </template>
            </q-btn>
            <q-btn
              v-if="step > 1"
              style="width: 9rem"
              :color="isImporting ? 'positive' : 'primary'"
              :label="isMove ? i18n.t('common.move') : i18n.t('common.copy')"
              :loading="isImporting"
              :disable="!canImport"
              class="q-ml-sm"
              @click="onImportClick"
            >
              <template #loading>
                <div class="q-ph-md row">
                  {{ isMove ? i18n.t('common.moving') : i18n.t('common.copying') }}
                  <q-spinner-gears class="on-right" />
                </div>
              </template>
            </q-btn>
            <q-btn
              v-if="step > 1"
              flat
              style="width: 9rem"
              color="primary"
              label="Back"
              class="q-ml-sm"
              :disable="isImporting"
              @click="onBackClick"
            />
          </div>
        </q-stepper-navigation>
      </template>
    </q-stepper>
  </dx-popup>
</template>
